import { jsPDF } from "jspdf";

const PRIMARY = [0, 47, 167]; // #002FA7
const BLACK = [10, 10, 10];
const GRAY = [134, 142, 150];
const GRAY_LIGHT = [222, 226, 230];
const ACCENT = [255, 215, 0];

const W = 210;
const H = 297;
const M = 18;

function header(doc, entreprise, title, sub) {
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, W, 28, "F");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("PAG · DOCUMENT RH", M, 11);
  doc.setFontSize(16);
  doc.text(title.toUpperCase(), M, 21);
  if (sub) {
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(sub, W - M, 21, { align: "right" });
  }
  // Accent strip
  doc.setFillColor(...ACCENT);
  doc.rect(0, 28, W, 1.2, "F");
}

function entrepriseBlock(doc, entreprise, y) {
  doc.setTextColor(...BLACK);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(entreprise.name || "Entreprise", M, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  const lines = [
    [entreprise.adresse, [entreprise.code_postal, entreprise.ville].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    entreprise.siret ? `SIRET ${entreprise.siret}` : null,
    entreprise.email,
    entreprise.telephone,
  ].filter(Boolean);
  lines.forEach((l, i) => doc.text(l, M, y + 5 + i * 4));
  return y + 5 + lines.length * 4 + 4;
}

function salarieBlock(doc, salarie, y) {
  doc.setDrawColor(...GRAY_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y);
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.setFont("helvetica", "bold");
  doc.text("DESTINATAIRE", M, y);
  y += 5;
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text(`${(salarie.prenom || "").trim()} ${(salarie.nom || "").trim()}`, M, y);
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  y += 5;
  const info = [
    salarie.code ? `Matricule ${salarie.code}` : null,
    salarie.poste ? `Poste : ${salarie.poste}` : null,
    salarie.contrat ? `Contrat ${salarie.contrat}` : null,
    salarie.adresse,
  ].filter(Boolean);
  info.forEach((l, i) => doc.text(l, M, y + i * 4));
  return y + info.length * 4 + 4;
}

function body(doc, paragraphs, y) {
  doc.setTextColor(...BLACK);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  paragraphs.forEach((p) => {
    const wrapped = doc.splitTextToSize(p, W - 2 * M);
    doc.text(wrapped, M, y);
    y += wrapped.length * 5 + 4;
  });
  return y;
}

function signature(doc, ville, dateStr, signatureLabel, y) {
  if (y > 240) { doc.addPage(); y = M; }
  y = Math.max(y + 10, 250);
  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  doc.text(`Fait à ${ville || "________"}, le ${dateStr}`, M, y);
  y += 8;
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text(signatureLabel, M, y);
  y += 10;
  doc.setDrawColor(...GRAY);
  doc.line(M, y, M + 60, y);
  return y;
}

function todayLong() {
  return new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

// -------- 1. CONTRAT CDI --------
export function generateContratCDIPDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Contrat à durée indéterminée");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);
  y = salarieBlock(doc, salarie, y);

  const dateEmbauche = options.date_debut || todayLong();
  const tauxHoraire = options.taux_horaire ?? salarie.taux_horaire ?? "";
  const dureeHebdo = options.duree_hebdo || 35;
  const lieu = options.lieu || entreprise.ville || "";
  const periodeEssai = options.periode_essai || 2;

  y = body(doc, [
    `Entre les soussignés ${entreprise.name || ""} (« l'Employeur »), représentée par sa direction, d'une part,`,
    `et ${salarie.prenom || ""} ${salarie.nom || ""} (« le Salarié »), d'autre part,`,
    `Il a été conclu le présent contrat de travail à durée indéterminée régi par les dispositions du Code du travail et de la convention collective applicable.`,
    `ARTICLE 1 — ENGAGEMENT. Le Salarié est engagé à compter du ${dateEmbauche} en qualité de ${salarie.poste || "salarié"}. L'engagement est soumis à la condition suspensive des résultats favorables de la visite d'information médicale.`,
    `ARTICLE 2 — PÉRIODE D'ESSAI. Une période d'essai de ${periodeEssai} mois est expressément prévue, renouvelable une fois pour une durée maximale équivalente.`,
    `ARTICLE 3 — DURÉE DU TRAVAIL. La durée hebdomadaire de travail est fixée à ${dureeHebdo} heures effectives, organisées selon le planning communiqué par l'Employeur.`,
    `ARTICLE 4 — RÉMUNÉRATION. Le Salarié percevra un salaire brut horaire de ${tauxHoraire || "____"} € pour son travail effectif, payé mensuellement, en sus des éventuelles primes prévues par la convention collective.`,
    `ARTICLE 5 — LIEU DE TRAVAIL. Le travail s'exerce principalement à ${lieu}, sous réserve de déplacements professionnels ponctuels.`,
    `ARTICLE 6 — OBLIGATIONS DU SALARIÉ. Le Salarié s'engage à respecter les directives, le règlement intérieur et les obligations de loyauté et de confidentialité inhérentes à son poste.`,
    `ARTICLE 7 — RUPTURE. À l'expiration de la période d'essai, le présent contrat ne pourra être rompu que dans le respect des règles légales et conventionnelles applicables.`,
  ], y);

  signature(doc, lieu, todayLong(), "Signature des deux parties (précédée de « Lu et approuvé »)", y);
  return doc;
}

// -------- 2. CONTRAT CDD --------
export function generateContratCDDPDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Contrat à durée déterminée");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);
  y = salarieBlock(doc, salarie, y);

  const dateDebut = options.date_debut || todayLong();
  const dateFin = options.date_fin || "________";
  const tauxHoraire = options.taux_horaire ?? salarie.taux_horaire ?? "";
  const motif = options.motif || "accroissement temporaire d'activité";
  const lieu = options.lieu || entreprise.ville || "";

  y = body(doc, [
    `Entre ${entreprise.name || ""} (« l'Employeur ») et ${salarie.prenom || ""} ${salarie.nom || ""} (« le Salarié »),`,
    `il a été convenu un contrat à durée déterminée conformément aux articles L.1242-1 et suivants du Code du travail.`,
    `ARTICLE 1 — OBJET ET MOTIF. Le présent contrat est conclu pour le motif suivant : ${motif}.`,
    `ARTICLE 2 — DURÉE. Le contrat débute le ${dateDebut} et prend fin le ${dateFin}, sauf renouvellement écrit par avenant ou rupture anticipée dans les conditions légales.`,
    `ARTICLE 3 — FONCTION. Le Salarié est engagé en qualité de ${salarie.poste || "salarié"}.`,
    `ARTICLE 4 — RÉMUNÉRATION. Le Salarié percevra un salaire brut horaire de ${tauxHoraire || "____"} €, ainsi que toutes primes et accessoires prévus par la convention collective. À l'issue du contrat, une indemnité de fin de contrat de 10 % du salaire brut total sera versée, sauf cas d'exclusion légale.`,
    `ARTICLE 5 — LIEU DE TRAVAIL. ${lieu}.`,
    `ARTICLE 6 — CONGÉS PAYÉS. Une indemnité compensatrice de congés payés sera versée à la fin du contrat, calculée conformément à la loi.`,
    `ARTICLE 7 — RUPTURE ANTICIPÉE. Le contrat ne pourra être rompu de façon anticipée que dans les cas prévus par la loi (accord des parties, faute grave, force majeure, embauche en CDI, inaptitude).`,
  ], y);

  signature(doc, lieu, todayLong(), "Signature des deux parties (précédée de « Lu et approuvé »)", y);
  return doc;
}

// -------- 3. LETTRE DE LICENCIEMENT --------
export function generateLicenciementPDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Lettre de licenciement");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);
  y = salarieBlock(doc, salarie, y);

  const motif = options.motif || "motif personnel";
  const detail = options.detail || "_________________________________________";
  const datePreavis = options.date_preavis || todayLong();
  const dureePreavis = options.duree_preavis || 2;

  y = body(doc, [
    `${salarie.prenom || ""} ${salarie.nom || ""},`,
    `Nous faisons suite à l'entretien préalable qui s'est tenu le ${datePreavis}, au cours duquel vous avez pu exposer vos observations.`,
    `Nous avons le regret de vous notifier par la présente votre licenciement pour ${motif}.`,
    `Les faits justifiant cette décision sont les suivants : ${detail}.`,
    `Le présent licenciement prend effet à la date de première présentation de cette lettre. Vous êtes redevable d'un préavis de ${dureePreavis} mois, sauf dispense de notre part.`,
    `Vous percevrez à l'issue de votre contrat les sommes correspondant à votre solde de tout compte, votre certificat de travail, l'attestation France Travail (ex-Pôle Emploi) et le récapitulatif d'épargne salariale.`,
    `Conformément aux dispositions légales, vous bénéficiez d'un délai pour contester la régularité ou le motif de cette décision devant le Conseil de Prud'hommes.`,
    `Nous vous prions d'agréer, ${salarie.prenom || ""} ${salarie.nom || ""}, l'expression de nos salutations.`,
  ], y);

  signature(doc, entreprise.ville || "____", todayLong(), "L'employeur", y);
  return doc;
}

// -------- 4. ACCUSÉ DE DÉMISSION --------
export function generateDemissionAccusePDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Accusé de réception — Démission");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);
  y = salarieBlock(doc, salarie, y);

  const dateDemission = options.date_demission || todayLong();
  const dureePreavis = options.duree_preavis || 1;
  const dateFin = options.date_fin || "________";

  y = body(doc, [
    `${salarie.prenom || ""} ${salarie.nom || ""},`,
    `Nous accusons réception de votre courrier en date du ${dateDemission} par lequel vous nous faites part de votre décision de démissionner de votre poste de ${salarie.poste || "salarié"}.`,
    `Nous prenons acte de votre démission. Conformément aux dispositions légales et conventionnelles applicables, votre préavis est fixé à ${dureePreavis} mois.`,
    `Sauf accord contraire entre les parties, votre dernier jour de présence dans l'entreprise sera le ${dateFin}.`,
    `Nous vous remettrons à cette date votre certificat de travail, votre solde de tout compte, l'attestation France Travail et, le cas échéant, le récapitulatif d'épargne salariale.`,
    `Nous vous remercions pour votre collaboration et vous souhaitons une pleine réussite dans vos futurs projets professionnels.`,
  ], y);

  signature(doc, entreprise.ville || "____", todayLong(), "L'employeur", y);
  return doc;
}

// -------- 5. CERTIFICAT DE TRAVAIL --------
export function generateCertificatTravailPDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Certificat de travail");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);

  const dateEntree = options.date_entree || "________";
  const dateSortie = options.date_sortie || todayLong();

  y += 8;
  y = body(doc, [
    `Je soussigné(e), représentant légal de l'entreprise ${entreprise.name || ""}, certifie que :`,
    `${salarie.prenom || ""} ${salarie.nom || ""}, demeurant ${salarie.adresse || "________"},`,
    `a été employé(e) au sein de notre société du ${dateEntree} au ${dateSortie} en qualité de ${salarie.poste || "salarié"}.`,
    `L'intéressé(e) nous quitte libre de tout engagement.`,
    `Le présent certificat est délivré pour servir et valoir ce que de droit.`,
  ], y);

  signature(doc, entreprise.ville || "____", todayLong(), "L'employeur", y);
  return doc;
}

// -------- 6. RECU POUR SOLDE DE TOUT COMPTE --------
export function generateSoldeToutComptePDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Reçu pour solde de tout compte");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);
  y = salarieBlock(doc, salarie, y);

  const dateSortie = options.date_sortie || todayLong();
  const salBrut = options.salaire_brut || "________";
  const salNet = options.salaire_net || "________";
  const primes = options.primes || "0";
  const congesPayes = options.conges_payes || "________";
  const total = options.total || "________";
  const motifSortie = options.motif_sortie || "fin de contrat";

  y = body(doc, [
    `Je soussigné(e) ${salarie.prenom || ""} ${salarie.nom || ""}, matricule ${salarie.code || "________"}, reconnais avoir reçu de l'entreprise ${entreprise.name || ""} pour solde de tout compte, à la suite de ${motifSortie} intervenue le ${dateSortie}, les sommes détaillées ci-après :`,
    `• Salaire brut du mois : ${salBrut} €`,
    `• Salaire net : ${salNet} €`,
    `• Indemnité compensatrice de congés payés : ${congesPayes} €`,
    `• Primes / gratifications : ${primes} €`,
    `• TOTAL NET VERSÉ : ${total} €`,
    `Le présent reçu, établi en deux exemplaires dont un remis au salarié, a été signé par les deux parties conformément à l'article L.1234-20 du Code du travail. Il peut être dénoncé par lettre recommandée dans un délai de 6 mois à compter de sa signature.`,
    `Attesté sincère et véritable.`,
  ], y);

  signature(doc, entreprise.ville || "____", todayLong(), "Signature du salarié (précédée de « Pour solde de tout compte »)", y);
  return doc;
}

// -------- 7. ATTESTATION EMPLOYEUR FRANCE TRAVAIL (simplifiée) --------
export function generateAttestationFranceTravailPDF({ entreprise, salarie, options = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  header(doc, entreprise, "Attestation employeur — France Travail");
  let y = 35;
  y = entrepriseBlock(doc, entreprise, y);
  y = salarieBlock(doc, salarie, y);

  const dateEntree = options.date_entree || "________";
  const dateSortie = options.date_sortie || todayLong();
  const motifSortie = options.motif_sortie || "fin de contrat";
  const derniereRemuBrute = options.derniere_remu || "________";
  const preavisEffectue = options.preavis_effectue || "Oui";

  y = body(doc, [
    `L'entreprise ${entreprise.name || ""} (SIRET ${entreprise.siret || "________"}) atteste avoir employé :`,
    `${salarie.prenom || ""} ${salarie.nom || ""} (matricule ${salarie.code || "________"})`,
    `du ${dateEntree} au ${dateSortie} en qualité de ${salarie.poste || "salarié"} sous contrat ${salarie.contrat || "CDI"}.`,
    `Motif de la rupture : ${motifSortie}.`,
    `Préavis : ${preavisEffectue}.`,
    `Dernière rémunération brute mensuelle : ${derniereRemuBrute} €.`,
    `Cette attestation est remise à l'intéressé(e) pour faire valoir ses droits, notamment auprès de France Travail (anciennement Pôle Emploi).`,
    `Note : cette version PDF est indicative — le document officiel doit être transmis via le portail employeur France Travail.`,
  ], y);

  signature(doc, entreprise.ville || "____", todayLong(), "L'employeur", y);
  return doc;
}

export const RH_TEMPLATES = [
  { id: "cdi", label: "Contrat CDI", icon: "FileText", description: "Contrat à durée indéterminée standard", generator: generateContratCDIPDF },
  { id: "cdd", label: "Contrat CDD", icon: "FileClock", description: "Contrat à durée déterminée avec motif", generator: generateContratCDDPDF },
  { id: "licenciement", label: "Lettre de licenciement", icon: "FileWarning", description: "Notification motivée de rupture", generator: generateLicenciementPDF },
  { id: "demission", label: "Accusé démission", icon: "FileCheck", description: "Réponse à une démission de salarié", generator: generateDemissionAccusePDF },
  { id: "certificat", label: "Certificat de travail", icon: "FileBadge", description: "À remettre en fin de contrat", generator: generateCertificatTravailPDF },
  { id: "solde", label: "Solde de tout compte", icon: "FileCheck", description: "Reçu pour solde de tout compte (fin de contrat)", generator: generateSoldeToutComptePDF },
  { id: "france_travail", label: "Attestation France Travail", icon: "FileText", description: "Attestation employeur pour France Travail", generator: generateAttestationFranceTravailPDF },
];
